import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LoginInput, requestLogin } from "./api/login";
import { checkJwtExpiration } from "./jwt";
import { m2mAuthTokenApi } from "./client";
import {
  defaultStorageAdapter,
  type PersistenceStorageAdapter,
} from "./persistenceDatabase";
import { createRefreshAuth } from "./refreshAuth";
import { createClearAuth } from "./clearAuth";

export type { PersistenceStorageAdapter };

interface AuthenticationState {
  token: string;
  authenticated: boolean;
  login: (input: LoginInput) => Promise<{ token?: string; error?: Error }>;
  logout: () => void;
  updateToken: (token: string) => Promise<{ error?: Error }>;
  revalidating: boolean;
  isLoginRequired: boolean;
}

const AuthenticationContext = createContext<AuthenticationState | undefined>(
  undefined,
);

/**
 * 認証状態を管理するProvider。
 * storageAdapterをカスタマイズする場合、参照が不変になるようにしてください。
 * 参照が変わるとトークン更新処理が再実行されます。
 */
export const M2mAuthenticationProvider = ({
  children,
  storageAdapter = defaultStorageAdapter,
}: {
  children: React.ReactNode;
  storageAdapter?: PersistenceStorageAdapter;
}) => {
  const [token, setToken] = useState(m2mAuthTokenApi.getCache() ?? "");

  const authenticated = useMemo(() => !!token, [token]);

  const tryCheckAuth = useCallback(async () => {
    const authToken = await m2mAuthTokenApi.get();
    if (!(authToken !== null && checkJwtExpiration(authToken))) {
      void m2mAuthTokenApi.clear();
      setToken("");
    } else {
      void m2mAuthTokenApi.set(authToken);
      setToken(authToken);
    }
  }, []);

  useEffect(() => {
    // tryCheckAuthは依存配列が空のuseCallbackで定義されているため、
    // 常に同じ関数参照を返し、このuseEffectは初回レンダリング後のみ実行される。
    // レンダリング後に一度認証状態を確認する意図的な動作のため、このルールを無効化。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void tryCheckAuth();
  }, [tryCheckAuth]);

  const login = useCallback(
    async (input: LoginInput) => {
      const { data: auth, error } = await requestLogin(input);
      if (auth) {
        const authToken = auth.accessToken;

        setToken(authToken);
        await m2mAuthTokenApi.set(authToken);
        await storageAdapter.set(auth);
      }
      return {
        error,
        token: auth ? auth.accessToken : undefined,
      };
    },
    [storageAdapter],
  );

  const [revalidating, setRevalidating] = useState(true);

  useEffect(() => {
    const refreshAuth = createRefreshAuth(storageAdapter);
    const callback = async () => {
      const authToken = await refreshAuth();
      if (authToken !== null) {
        setToken(authToken);
      }
      setRevalidating(false);
    };
    void callback();
    // Update auth every 3 hours
    const timer = setInterval(callback, 3 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, [storageAdapter]);

  const logout = useCallback(async () => {
    const clearAuth = createClearAuth(storageAdapter);
    await clearAuth();
    window.location.reload();
  }, [storageAdapter]);

  const updateToken = useCallback(async (token: string) => {
    // checkJwtExpirationはトークンが有効(未失効)ならtrueを返す
    const isTokenValid = checkJwtExpiration(token);
    if (!isTokenValid) {
      return {
        error: new Error("M2MAuthToken is expired or invalid"),
      };
    }
    await m2mAuthTokenApi.set(token);
    setToken(token);
    return { error: undefined };
  }, []);

  const state = useMemo(() => {
    return {
      login,
      logout,
      token,
      authenticated,
      updateToken,
      revalidating,
      isLoginRequired: authenticated === false && revalidating === false,
    } satisfies AuthenticationState;
  }, [authenticated, login, logout, revalidating, token, updateToken]);
  return (
    <AuthenticationContext.Provider value={state}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthenticationContext);
  if (!ctx) {
    throw new Error("AuthenticationProvider is not found");
  }
  return ctx;
};
